/* ============================================================================
   tne-header.js — Lógica do header TopBioEuropa

   Responsabilidades:
   - Toggle do mobile menu (hamburger -> overlay full-screen).
   - Adiciona classe .tne-header--scrolled após scroll > 50px.
   - Mede a altura real do header e expõe --tne-header-h (compensa overlay
     do position: fixed com padding-top no body).
   - Atualiza --tne-progress (0..1) em rAF para a barra de scroll dourada.
   - Fecha menu mobile ao clicar num link da navegação.
   - Fecha menu mobile ao redimensionar para desktop (>=992px) e na tecla Esc.
   ============================================================================ */

(function () {
  'use strict';

  var SCROLL_THRESHOLD = 50;
  var DESKTOP_BREAKPOINT = 992;
  var BODY_OPEN_CLASS = 'tne-header-mobile-open';

  var header = document.querySelector('[data-tne-header]');
  if (!header) return;

  var toggle = header.querySelector('[data-tne-menu-toggle]');
  var mobileMenu = header.querySelector('[data-tne-mobile-menu]');
  var progressFill = header.querySelector('[data-tne-header-progress-fill]');

  // ---------- Header height -> --tne-header-h ----------
  // Mede a altura real do header (incluindo a progress bar) e expõe como
  // custom property no <html>. Usada pelo CSS em body { padding-top }.
  function setHeaderHeight() {
    var h = header.offsetHeight;
    if (h > 0) {
      document.documentElement.style.setProperty('--tne-header-h', h + 'px');
    }
  }

  // ---------- Scroll progress (rAF throttled) ----------
  var ticking = false;
  function updateScrollState() {
    ticking = false;

    // Toggle scrolled class.
    if (window.scrollY > SCROLL_THRESHOLD) {
      header.classList.add('tne-header--scrolled');
    } else {
      header.classList.remove('tne-header--scrolled');
    }

    // Progress bar fill (0..1).
    if (progressFill) {
      var doc = document.documentElement;
      var max = (doc.scrollHeight - window.innerHeight);
      var ratio = max > 0 ? window.scrollY / max : 0;
      if (ratio < 0) ratio = 0;
      else if (ratio > 1) ratio = 1;
      progressFill.style.setProperty('--tne-progress', ratio.toFixed(4));
    }
  }

  function handleScroll() {
    if (!ticking) {
      requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  }

  window.addEventListener('scroll', handleScroll, { passive: true });
  updateScrollState(); // Estado inicial caso a página carregue já com scroll.

  // Resize / orientation -> recalcular altura e progresso.
  window.addEventListener('resize', function () {
    setHeaderHeight();
    handleScroll();
  }, { passive: true });

  // Mede assim que possível e novamente após load (fontes, imagens etc.).
  setHeaderHeight();
  if (document.readyState !== 'complete') {
    window.addEventListener('load', setHeaderHeight, { once: true });
  }

  // ---------- Mobile menu open/close ----------
  function openMenu() {
    if (!mobileMenu || !toggle) return;
    mobileMenu.dataset.open = 'true';
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Fechar menu de navegação');
    document.body.classList.add(BODY_OPEN_CLASS);
  }

  function closeMenu() {
    if (!mobileMenu || !toggle) return;
    mobileMenu.dataset.open = 'false';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Abrir menu de navegação');
    document.body.classList.remove(BODY_OPEN_CLASS);
  }

  if (toggle && mobileMenu) {
    toggle.addEventListener('click', function () {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Fechar ao clicar em qualquer link dentro do overlay.
    var mobileLinks = mobileMenu.querySelectorAll('a');
    Array.prototype.forEach.call(mobileLinks, function (link) {
      link.addEventListener('click', closeMenu);
    });

    // Fechar com Escape (acessibilidade).
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        closeMenu();
        toggle.focus();
      }
    });
  }

  // ---------- Resize -> fechar menu ao passar para desktop ----------
  var resizeTimer = null;
  window.addEventListener(
    'resize',
    function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (window.innerWidth >= DESKTOP_BREAKPOINT) {
          closeMenu();
        }
      }, 100);
    },
    { passive: true }
  );

  // ---------- Profile dropdown (logged-in customer) ----------
  // Toggle, fecho ao clicar fora, fecho com Escape, foco no primeiro item.
  var profile = header.querySelector('[data-tne-profile]');
  if (profile) {
    var profileToggle = profile.querySelector('[data-tne-profile-toggle]');
    var profileMenu = profile.querySelector('.tne-header__profile-menu');

    function openProfile() {
      profile.setAttribute('data-open', 'true');
      profileToggle.setAttribute('aria-expanded', 'true');
      profileMenu.removeAttribute('hidden');
    }

    function closeProfile() {
      profile.setAttribute('data-open', 'false');
      profileToggle.setAttribute('aria-expanded', 'false');
      profileMenu.setAttribute('hidden', '');
    }

    function toggleProfile() {
      if (profile.getAttribute('data-open') === 'true') {
        closeProfile();
      } else {
        openProfile();
      }
    }

    profileToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleProfile();
    });

    // Fechar ao clicar fora do dropdown.
    document.addEventListener('click', function (e) {
      if (
        profile.getAttribute('data-open') === 'true' &&
        !profile.contains(e.target)
      ) {
        closeProfile();
      }
    });

    // Fechar com Escape e devolver foco ao toggle.
    document.addEventListener('keydown', function (e) {
      if (
        e.key === 'Escape' &&
        profile.getAttribute('data-open') === 'true'
      ) {
        closeProfile();
        profileToggle.focus();
      }
    });

    // Setas para navegar entre itens do menu (acessibilidade).
    profileMenu.addEventListener('keydown', function (e) {
      var items = Array.prototype.slice.call(
        profileMenu.querySelectorAll('[role="menuitem"]')
      );
      if (!items.length) return;
      var current = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        items[(current + 1) % items.length].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        items[(current - 1 + items.length) % items.length].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        items[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        items[items.length - 1].focus();
      }
    });
  }
})();
